import type { DocChapter, DocSubsection, UserDocumentation } from '@/types/help-docs';

const TABLE_MARKER = '\n\n@@TABLE@@\n';

export function findChapterById(chapters: DocChapter[], chapterId: string): DocChapter | undefined {
  return chapters.find((chapter) => chapter.id === chapterId);
}

/** Find the chapter that contains a subsection (used for contextual deep links). */
export function findChapterBySubsectionId(chapters: DocChapter[], subsectionId: string): DocChapter | undefined {
  return chapters.find((chapter) => chapter.subsections.some((subsection) => subsection.id === subsectionId));
}

export function resolveActiveChapterId(
  chapters: DocChapter[],
  chapterParam: string | null,
  hashSubsectionId: string
): string {
  if (chapterParam && findChapterById(chapters, chapterParam)) {
    return chapterParam;
  }
  if (hashSubsectionId) {
    const fromHash = findChapterBySubsectionId(chapters, hashSubsectionId);
    if (fromHash) {
      return fromHash.id;
    }
  }
  return chapters[0]?.id ?? '';
}

export function parseDocContent(content: string): { mainContent: string; tableContent: string | null } {
  if (!content.includes(TABLE_MARKER)) {
    return { mainContent: content, tableContent: null };
  }
  const [mainContent, tableContent] = content.split(TABLE_MARKER);
  return { mainContent, tableContent: tableContent ?? null };
}

export function getUserDocChapters(docs: UserDocumentation): DocChapter[] {
  return docs.chapters;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type HighlightPart = { text: string; match: boolean };

/** Split text into contiguous parts for search-term highlighting. */
export function splitHighlightParts(text: string, query: string): HighlightPart[] {
  const trimmed = query.trim();
  if (!trimmed || !text) {
    return text ? [{ text, match: false }] : [];
  }

  const regex = new RegExp(escapeRegExp(trimmed), 'gi');
  const parts: HighlightPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) != null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), match: false });
    }
    parts.push({ text: match[0], match: true });
    lastIndex = match.index + match[0].length;
    if (match[0].length === 0) {
      regex.lastIndex += 1;
    }
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), match: false });
  }

  return parts.length > 0 ? parts : [{ text, match: false }];
}

function includesIgnoreCase(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export function subsectionMatchesQuery(subsection: DocSubsection, query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  return includesIgnoreCase(subsection.title, trimmed) || includesIgnoreCase(subsection.content, trimmed);
}

export function chapterMatchesQuery(chapter: DocChapter, query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  return (
    includesIgnoreCase(chapter.title, trimmed) ||
    includesIgnoreCase(chapter.summary, trimmed) ||
    chapter.subsections.some((subsection) => subsectionMatchesQuery(subsection, trimmed))
  );
}

/**
 * Filter chapters for docs search. Chapters whose title/summary match keep all
 * subsections; otherwise only matching subsections are kept.
 */
export function filterDocChapters(chapters: DocChapter[], query: string): DocChapter[] {
  const trimmed = query.trim();
  if (!trimmed) return chapters;

  return chapters
    .filter((chapter) => chapterMatchesQuery(chapter, trimmed))
    .map((chapter) => {
      const chapterMetaMatch =
        includesIgnoreCase(chapter.title, trimmed) || includesIgnoreCase(chapter.summary, trimmed);
      if (chapterMetaMatch) {
        return chapter;
      }
      return {
        ...chapter,
        subsections: chapter.subsections.filter((subsection) => subsectionMatchesQuery(subsection, trimmed))
      };
    });
}

/** First subsection id to scroll to for a chapter under the current query. */
export function firstMatchingSubsectionId(chapter: DocChapter, query: string): string | null {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const directMatch = chapter.subsections.find((subsection) => subsectionMatchesQuery(subsection, trimmed));
  if (directMatch) return directMatch.id;
  return chapter.subsections[0]?.id ?? null;
}
